import { makeStyles } from "@material-ui/core/styles";

/**
 * Styles for the technician installation photos UI. Designed to be
 * mobile-friendly and touch-accessible (Requirement 12), matching the mockups:
 * Take Photo / Choose From Gallery actions, a scrollable thumbnail list, and a
 * full-screen single-photo viewer with a bottom CLOSE bar and a trash icon.
 */
export const useStyles = makeStyles((theme) => ({
  "@global": {
    ".dialog-confirmation": {
      zIndex: `${theme.zIndex.modal + 2} !important`,
    },
  },
  /* New-page layout with a blue "Details" navigation bar on top */
  page: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  navBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, 1.5),
  },
  navBackButton: {
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(0.5),
  },
  navTitle: {
    fontWeight: 600,
    color: theme.palette.primary.contrastText,
  },
  pageBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
  },
  actionsBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  actionButtonWrapper: {
    flex: "1 1 auto",
    display: "inline-flex",
  },
  actionButton: {
    width: "100%",
    minHeight: 44, // touch-friendly target
    textTransform: "none",
  },
  hiddenInput: {
    display: "none",
  },
  inlineError: {
    marginTop: theme.spacing(0.5),
  },
  inlineLoading: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },

  /* Thumbnail list */
  photoList: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  emptyState: {
    padding: theme.spacing(2, 0),
    textAlign: "center",
  },
  thumbnail: {
    cursor: "pointer",
    borderRadius: 4,
    overflow: "hidden",
    backgroundColor: theme.palette.grey[100],
    aspectRatio: "4 / 3",
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  thumbnailLoading: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  /* Camera capture */
  cameraContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
  },
  cameraVideo: {
    width: "100%",
    maxHeight: 420,
    backgroundColor: "#000",
    borderRadius: 4,
  },
  cameraPreview: {
    width: "100%",
    maxHeight: 420,
    objectFit: "contain",
    backgroundColor: "#000",
    borderRadius: 4,
  },
  cameraLoading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  cameraActions: {
    display: "flex",
    gap: theme.spacing(1),
    "& > *": {
      flex: "1 1 auto",
      minHeight: 44,
    },
  },
 
  /* Full-screen viewer: fixed overlay that covers the whole viewport on both
     mobile and desktop. */
  viewerContainer: {
    position: "fixed",
    inset: 0,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100vw",
    height: "100vh",
    maxHeight: "100vh",
    "@supports (height: 100dvh)": {
      height: "100dvh",
      maxHeight: "100dvh",
    },
    zIndex: theme.zIndex.modal + 1,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    padding: 0,
    boxSizing: "border-box",
  },
  viewerImageArea: {
    position: "relative",
    flex: "1 1 auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "auto",
    minHeight: 0,
    width: "100%",
  },
  viewerImage: {
    width: "100%",
    height: "auto",
    maxHeight: "100%",
    objectFit: "contain",
    display: "block",
  },
  viewerLoading: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  viewerError: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  viewerErrorActions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  viewerActions: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: `calc(${theme.spacing(2)}px + env(safe-area-inset-bottom, 0px))`,
  },
  closeButton: {
    minHeight: 44,
    minWidth: 160,
    textTransform: "none",
  },
  viewerDeleteButton: {
    color: theme.palette.text.primary,
  },
}));
